import { Injectable, inject } from '@angular/core';
import type { EAN } from '../shared/ean';
import type { NonEmpty } from '../shared/non-empty';
import {
  ObservationAcquisitionService,
  type ProductFetchRequest,
} from './observation-acquisition.service';
import { isFreshRow, isFreshUpd } from './observation-freshness';
import {
  type CacheBatchResult,
  type CacheKey,
  type CacheRowResult,
  ObservationRepository,
} from './observation-repository';

const PROBE_CHUNK = 10;
const PROBE_CONCURRENCY = 5;
const FIRST_POLL_MS = 500;
const POLL_BACKOFF = 1.5;
const MAX_POLL_MS = 2_000;
// Measured scrape throughput is about two products a second.
const BASE_DEADLINE_MS = 6_000;
const DEADLINE_MS_PER_PRODUCT = 500;
const MAX_DEADLINE_MS = 180_000;

type RefreshProgress = { readonly total: number; readonly refreshed: readonly EAN[] };

type RefreshOptions = {
  readonly signal: AbortSignal;
  readonly deadlineMs?: number;
  readonly onProgress?: (progress: RefreshProgress) => void | Promise<void>;
  /**
   * The months the caller shows plus their lead-in, as `YYYY-MM`. Both row reads are bounded to
   * them; the freshness probe stays unfiltered. Omitted means whole history.
   */
  readonly monthsYM?: NonEmpty<string>;
};

type RefreshResult = {
  readonly rows: CacheBatchResult;
  readonly refreshed: readonly EAN[];
  readonly pending: readonly EAN[];
  readonly unscrapable: readonly EAN[];
};

/**
 * Brings stale observation rows up to date: requests a scrape for the products still stale in
 * the database, waits for fresh rows until a deadline, and re-reads them through the repository.
 */
@Injectable()
export class ObservationRefreshService {
  private readonly repo = inject(ObservationRepository);
  private readonly acquisition = inject(ObservationAcquisitionService);

  /**
   * Resolves with the current row of every requested EAN that has one, the EANs re-read as fresh
   * during the call, the enqueued EANs still stale at the deadline, and the stale EANs without a
   * page. `onProgress` is awaited once the scrapes are requested and after each poll
   * round. Rejects with `signal.reason` on abort, and with a failed insert or probe unchanged.
   */
  async refresh(
    requests: readonly ProductFetchRequest[],
    { signal, deadlineMs, onProgress, monthsYM }: RefreshOptions
  ): Promise<RefreshResult> {
    signal.throwIfAborted();
    const pageOf = new Map<EAN, string | null>();
    for (const { ean, page } of requests) if (!pageOf.has(ean)) pageOf.set(ean, page);
    const eans = [...pageOf.keys()];

    const rows = new Map<CacheKey, CacheRowResult>(
      await this.repo.readBatch(eans, monthsYM, undefined, signal)
    );
    signal.throwIfAborted();
    const now = Date.now();
    const stale = eans.filter((ean) => !isFreshRow(rows.get(ean), now));
    const refreshed: EAN[] = [];
    if (!stale.length) return { rows, refreshed, pending: [], unscrapable: [] };

    const landed = await this.probeFresh(stale, signal);
    const unscrapable = await this.acquisition.requestProductFetches(
      stale
        .filter((ean) => !landed.has(ean))
        .map((ean) => ({ ean, page: pageOf.get(ean) ?? null })),
      signal
    );
    signal.throwIfAborted();
    const skipped = new Set(unscrapable);
    let pending = stale.filter((ean) => !landed.has(ean) && !skipped.has(ean));
    const total = landed.size + pending.length;
    if (!total) return { rows, refreshed, pending, unscrapable };
    const deadline =
      Date.now() +
      (deadlineMs ??
        Math.min(MAX_DEADLINE_MS, BASE_DEADLINE_MS + DEADLINE_MS_PER_PRODUCT * pending.length));

    const reread = async (fresh: readonly EAN[]): Promise<void> => {
      if (fresh.length) {
        const landedRows = await this.repo.readBatch(fresh, monthsYM, undefined, signal);
        signal.throwIfAborted();
        for (const ean of fresh) {
          const row = landedRows.get(ean);
          if (row) rows.set(ean, row);
          else rows.delete(ean);
        }
        refreshed.push(...fresh);
      }
      await onProgress?.({ total, refreshed: [...refreshed] });
      signal.throwIfAborted();
    };

    await reread([...landed]);
    let delay = FIRST_POLL_MS;
    while (pending.length && Date.now() < deadline) {
      await sleep(Math.min(delay, deadline - Date.now()), signal);
      signal.throwIfAborted();
      delay = Math.min(Math.round(delay * POLL_BACKOFF), MAX_POLL_MS);
      const fresh = await this.probeFresh(pending, signal);
      pending = pending.filter((ean) => !fresh.has(ean));
      await reread([...fresh]);
    }
    return { rows, refreshed, pending, unscrapable };
  }

  private async probeFresh(eans: readonly EAN[], signal: AbortSignal): Promise<ReadonlySet<EAN>> {
    const sorted = [...eans].sort();
    const asked = new Map<string, EAN>(sorted.map((ean) => [ean, ean]));
    const chunks: EAN[][] = [];
    for (let i = 0; i < sorted.length; i += PROBE_CHUNK) {
      chunks.push(sorted.slice(i, i + PROBE_CHUNK));
    }
    const fresh = new Set<EAN>();
    for (let i = 0; i < chunks.length; i += PROBE_CONCURRENCY) {
      const answers = await Promise.all(
        chunks.slice(i, i + PROBE_CONCURRENCY).map((chunk) => this.repo.probeUpdates(chunk, signal))
      );
      signal.throwIfAborted();
      const now = Date.now();
      for (const { cKey, upd } of answers.flat()) {
        const ean = asked.get(cKey);
        // The newest upd decides, and any fresh upd is at least as new as the newest stale one.
        if (ean && isFreshUpd(upd, now)) fresh.add(ean);
      }
    }
    return fresh;
  }
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(signal.reason);
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}
